import { Component, DestroyRef, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, SelectionChangedEvent } from 'ag-grid-community';
import { defaultRowSelection } from '../../app/configs/ag-grid.config';

import { FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import huLocale from '@fullcalendar/core/locales/hu';

import { KomplexumService } from '../../services/komplexum.service';
import { KomplexumVmForList } from '../../models/komplexum.model';
import { HelysegVmForList } from '../../models/helyseg.model';

@Component({
    selector: 'rendelo-komplexum-list',
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatCardModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        AgGridModule,
        FullCalendarModule,
    ],
    templateUrl: './komplexum-list.component.html',
    styleUrls: ['./komplexum-list.component.scss']
})
export class KomplexumListComponent {

    private destroyRef = inject(DestroyRef);

    colDefs: ColDef[] = [
        { field: 'id', headerName: 'ID', width: 100, sortable: true },
        { field: 'nev', headerName: 'Megnevezés', sortable: true, flex: 1 }
    ];

    helysegColDefs: ColDef[] = [
        { field: 'nev', headerName: 'Megnevezés', sortable: true, flex: 1 },
        { field: 'epulet', headerName: 'Épület', width: 120 },
        { field: 'emelet', headerName: 'Emelet', width: 100 },
        { field: 'ajto', headerName: 'Ajtó', width: 100 },
    ];

    private gridApi?: GridApi;
    selectedKomplexum: KomplexumVmForList | null = null;
    selectedHelyszin: HelysegVmForList | null = null;
    helysegek: HelysegVmForList[] = [];
    helysegekLoading = false;

    rowSelectionConfig = defaultRowSelection;
    helysegRowSelectionConfig = defaultRowSelection;

    calendarOptions: CalendarOptions = {
        plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        locale: huLocale,
        headerToolbar: {
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
        },
        height: '100%',
        expandRows: true,
        slotMinTime: '07:00:00',
        slotMaxTime: '20:00:00',
        events: []
    };

    constructor(
        private komplexumService: KomplexumService,
        private snackBar: MatSnackBar,
        private router: Router
    ) {
        effect(() => {
            const errorMessage = this.komplexumService.error();
            if (errorMessage) {
                this.snackBar.open(errorMessage, 'Bezár', {
                    duration: 10000,
                    panelClass: ['error-snackbar'],
                });
            }
        });
    }

    get loading() {
        return this.komplexumService.loading;
    }

    get komplexumok() {
        return this.komplexumService.komplexumok;
    }

    onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
    }

    onSelectionChanged(event: SelectionChangedEvent) {
        const selectedRows = event.api.getSelectedRows();
        this.selectedKomplexum = selectedRows.length > 0 ? selectedRows[0] : null;
        this.selectedHelyszin = null;

        this.helysegek = [];
        if (this.selectedKomplexum) {
            this.helysegekLoading = true;
            this.komplexumService.getHelysegek(this.selectedKomplexum.id)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (helysegek) => {
                        this.helysegek = helysegek;
                        this.helysegekLoading = false;
                    },
                    error: () => {
                        this.helysegekLoading = false;
                    }
                });
        }
    }

    onHelyszinSelectionChanged(event: SelectionChangedEvent) {
        const selectedRows = event.api.getSelectedRows();
        this.selectedHelyszin = selectedRows.length > 0 ? selectedRows[0] : null;
    }
}
